<template>
  <div>
    <LeftSidebar
      :visible="visible"
      baseClass="add-term-wrapper"
      :title="$lang.newCenterExecute"
      :hasHeaderRightIcon="false"
    >
      <template>
        <div class="padding-16" v-if="visible">
          <Input
            :title="$lang.title"
            :label="$lang.title"
            :placeholder="$lang.title"
            :value="form.title"
            name="title"
            id="title"
            type="text"
            :vuelidateObj="$v.form.title"
            @input="input"
            @focus="resetValidation"
            @blur="checkValidation"
          />
          <Dropdown
            :title="$lang.company"
            :label="$lang.company"
            :placeholder="$lang.company"
            :value="form.company"
            name="company"
            id="company"
            :vuelidateObj="$v.form.company"
            :asyncSearch="getCompanyCombo"
            :insideFeature="true"
            @input="input"
            @focus="resetValidation"
            @blur="checkValidation"
            @change="getTermOptions"
          />
          <Dropdown
            :title="$lang.selectTerm"
            :label="$lang.selectTerm"
            :placeholder="$lang.selectTerm"
            :value="form.term"
            name="term"
            id="term"
            :vuelidateObj="$v.form.term"
            :options="termOptions"
            :loading="termLoading"
            @input="input"
            @focus="resetValidation"
            @blur="checkValidation"
            @change="getTemplateOptions"
          />
          <Dropdown
            :title="$lang.template"
            :label="$lang.template"
            :placeholder="$lang.template"
            :value="form.template"
            name="template"
            id="template"
            :vuelidateObj="$v.form.template"
            :loading="templateLoading"
            :options="templateOptions"
            @input="input"
            @focus="resetValidation"
            @blur="checkValidation"
            @change="getAssessmentPlanOptions"
          />
          <Dropdown
            :title="$lang.assessmentPlan"
            :label="$lang.assessmentPlan"
            :placeholder="$lang.assessmentPlan"
            :value="form.assessmentPlan"
            name="assessmentPlan"
            id="assessmentPlan"
            :vuelidateObj="$v.form.assessmentPlan"
            :loading="assessmentPlanLoading"
            :options="assessmentPlanOptions"
            @input="input"
            @focus="resetValidation"
            @blur="checkValidation"
          />
          <DatePicker
            :title="$lang.startDate"
            :label="$lang.startDate"
            :placeholder="$lang.startDate"
            :value="form.startDate"
            name="startDate"
            id="startDate"
            rightIcon="calendar-clock"
            displayFormat="jYYYY/jMM/jDD"
            format="YYYY-MM-DD"
            type="date"
            :popover="true"
            :simple="true"
            :autoSubmit="true"
            :hasEraseIcon="true"
            :vuelidateObj="$v.form.startDate"
            @input="input"
            @focus="resetValidation"
            @blur="checkValidation"
            @erase="input"
          />
          <Checkbox
            :label="$lang.floatingByStartTime"
            :value="form.floatingByStartTime"
            :infoTooltip="$lang.floatingByStartTimeTooltip"
            name="floatingByStartTime"
            hoverClass=""
            @input="input"
          />
        </div>
      </template>
      <template #footer>
        <Button
          variant="filled-complementary"
          :text="$lang.cancel"
          class="mobile-width-percent-100 tablet-width-percent-100"
          contentClass="justify-content-center"
          @click="handleClose"
        />
        <Button
          v-if="checkPermission(permissions, ['admin', 'edit'])"
          variant="primary"
          :text="$lang.continue"
          class="mobile-width-percent-100 tablet-width-percent-100"
          contentClass="justify-content-center"
          :loading="loading"
          @click="handleSubmit"
        />
      </template>
    </LeftSidebar>
  </div>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import LeftSidebar from "@/components/re-design/sidebar/LeftSidebar.vue";
import {
  Input,
  Dropdown,
  DatePicker,
  Checkbox,
  Button,
} from "@/components/re-design/forms";
import { required } from "vuelidate/lib/validators";
import { pathOr } from "ramda";
import { checkPermission, generateError, toastAlert } from "@/shared/utils";
import {
  getAssessmentPlanCombo,
  getCompanyCombo,
  getTemplateCombo,
  getTermCombo,
} from "@/services";
import { addCenterExecute } from "@/modules/kanoon/services";

export default {
  mixins: [inputMixin],
  inject: ["goToCenterExecuteEdit"],
  components: { LeftSidebar, Input, Dropdown, DatePicker, Checkbox, Button },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    permissions: {
      type: Array,
      default: () => [],
    },
  },
  validations() {
    return {
      form: {
        title: { required },
        company: { required },
        term: { required },
        template: { required },
        assessmentPlan: { required },
        startDate: { required },
      },
    };
  },
  data() {
    return {
      form: {
        title: null,
        company: null,
        term: null,
        template: null,
        assessmentPlan: null,
        startDate: null,
        floatingByStartTime: false,
      },
      loading: false,
      termLoading: false,
      termOptions: [],
      templateLoading: false,
      templateOptions: [],
      assessmentPlanLoading: false,
      assessmentPlanOptions: [],
    };
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (value) {
          // reset all field of form (this method imported of input mixin)
          this.resetForm();
        }
      },
    },
  },
  methods: {
    checkPermission,
    getCompanyCombo,
    handleClose() {
      this.$emit("handleClose");
      this.resetForm();
    },
    handleReloadList() {
      this.$emit("handleReloadList");
    },
    async getTermOptions() {
      const companyCode = pathOr(null, ["company", "id"], this.form);
      if (!companyCode) return;

      try {
        this.termLoading = true;
        const extraQueryString = { company_code: companyCode };
        const response = await getTermCombo(null, extraQueryString);
        const { options } = response;
        this.termOptions = options.map((option) => {
          return {
            ...option,
            id: pathOr(null, ["code"], option),
          };
        });
      } catch (error) {
        generateError(error);
      } finally {
        this.termLoading = false;
      }
    },
    async getTemplateOptions() {
      const companyCode = pathOr(null, ["company", "id"], this.form);
      if (!companyCode) return;

      try {
        this.templateLoading = true;
        const queryString = {
          "company_code[]": companyCode,
          completed: true,
        };
        const response = await getTemplateCombo(queryString);
        const { options } = response;
        this.templateOptions = options;
      } catch (error) {
        generateError(error);
      } finally {
        this.templateLoading = false;
      }
    },
    async getAssessmentPlanOptions(item = null) {
      const templateId = pathOr(null, ["id"], item);
      if (!templateId) return;

      try {
        this.assessmentPlanLoading = true;
        const response = await getAssessmentPlanCombo(templateId);
        const { options } = response;
        this.assessmentPlanOptions = options;
      } catch (error) {
        generateError(error);
      } finally {
        this.assessmentPlanLoading = false;
      }
    },
    async handleSubmit() {
      this.$v.$touch();
      if (this.$v.$anyError) return;

      try {
        this.loading = true;
        const { title, startDate, floatingByStartTime } = this.form;
        const companyCode = pathOr(null, ["company", "id"], this.form);
        const termCode = pathOr(null, ["term", "id"], this.form);
        const assessmentPlanId = pathOr(
          null,
          ["assessmentPlan", "id"],
          this.form
        );
        const response = await addCenterExecute({
          title,
          companyCode,
          termCode,
          assessmentPlanId,
          startDate,
          floatingByStartTime: Number(floatingByStartTime),
        });
        const { message, item } = response;
        toastAlert(message);
        this.handleClose();
        this.goToCenterExecuteEdit(item);
      } catch (error) {
        generateError(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
